import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';
import TooltipTextPopupCustom from './child/TooltipTextPopupCustom';

const TableDataLayerAssigned = () => {
    const [complaints, setComplaints] = useState([]);
    const [zoneBreakdown, setZoneBreakdown] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [qrCodes, setQrCodes] = useState({});
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Cascading forest location filters
    const [forestZones, setForestZones] = useState([]);
    const [forestCircles, setForestCircles] = useState([]);
    const [forestDivisions, setForestDivisions] = useState([]);
    const [selectedZone, setSelectedZone] = useState('');
    const [selectedCircle, setSelectedCircle] = useState('');
    const [selectedDivision, setSelectedDivision] = useState('');

    const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
    const APP_BASE_URL = process.env.REACT_APP_URL;
    const navigate = useNavigate();
    const location = useLocation();

    // Fetch forest zones for dropdown
    useEffect(() => {
        const loadZones = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await axios.get(`${API_BASE_URL}/backend/generic/forestZone`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setForestZones(Array.isArray(res.data?.data) ? res.data.data : []);
            } catch (err) {
                console.error("Error fetching forest zones:", err);
            }
        };
        loadZones();
    }, [API_BASE_URL]);

    // Handle zone change -> load circles
    const handleZoneChange = async (zoneId) => {
        setSelectedZone(zoneId);
        setSelectedCircle('');
        setSelectedDivision('');
        setForestCircles([]);
        setForestDivisions([]);

        if (!zoneId) return;
        const zone = forestZones.find((z) => z._id === zoneId);
        if (!zone) return;

        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
                `${API_BASE_URL}/backend/districts/get-circles-and-divisions-by-zone`,
                { zone: zone.name },
                { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
            );
            if (res.data?.status) {
                setForestCircles(res.data.circles || []);
            }
        } catch (err) {
            console.error("Error fetching circles and divisions:", err);
            toast.error("Failed to load circles for the selected zone.");
        }
    };

    // Handle circle change -> load divisions
    const handleCircleChange = (circleId) => {
        setSelectedCircle(circleId);
        setSelectedDivision('');
        const circle = forestCircles.find((c) => c._id === circleId);
        setForestDivisions(circle?.divisions || []);
    };

    const handleDivisionChange = (divisionId) => {
        setSelectedDivision(divisionId);
    };

    const fetchComplaints = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const params = {};
            if (selectedZone) params.forestZone = selectedZone;
            if (selectedCircle) params.forestCircle = selectedCircle;
            if (selectedDivision) params.forestDivision = selectedDivision;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            if (searchTerm.trim()) params.searchQuery = searchTerm.trim();

            const response = await axios.get(`${API_BASE_URL}/backend/complaints/get-assigned-complaints`, {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.data.success) {
                const data = response.data.data || [];
                setComplaints(data);
                setZoneBreakdown(response.data.zoneBreakdown || []);
                generateQrCodes(data, APP_BASE_URL);
            } else {
                toast.error("Failed to fetch assigned complaints.");
            }
        } catch (error) {
            console.error("Error fetching assigned complaints:", error);
            toast.error("Error fetching assigned complaints.");
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, APP_BASE_URL, selectedZone, selectedCircle, selectedDivision, fromDate, toDate, searchTerm]);

    const generateQrCodes = async (complaintsData, appBase) => {
        const entries = await Promise.all(
            complaintsData.map(async (complaint) => {
                try {
                    const qrCodeData = `${appBase}/complains/view/${complaint._id}`;
                    const qrCodeUrl = await QRCode.toDataURL(JSON.stringify(qrCodeData));
                    return [complaint._id, qrCodeUrl];
                } catch (error) {
                    console.error("Error generating QR code:", error);
                    return [complaint._id, null];
                }
            })
        );
        setQrCodes(Object.fromEntries(entries));
    };

    useEffect(() => {
        fetchComplaints();
    }, [fetchComplaints]);

    useEffect(() => {
        if (location.state?.showToast) {
            toast.success("Action processed successfully!");
        }
    }, [location.state]);

    const getPriorityClasses = (priority) => {
        switch (priority) {
            case "Low":
                return {
                    bg: 'bg-gradient-start-3',
                    border: 'border-info-300',
                    text: 'text-info-600',
                    glow: 'info-glow',
                    pulse: 'info-pulse',
                    wave: 'from-info-400 to-info-100',
                    ripple: 'info-100',
                    badge: 'bg-info-focus text-info-600',
                    gradient: 'bg-dark-info-gradient',
                    tooltip: 'tooltip-info'
                };
            case "Medium":
                return {
                    bg: 'bg-gradient-start-4',
                    border: 'border-success-300',
                    text: 'text-success-600',
                    glow: 'success-glow',
                    pulse: 'success-pulse',
                    wave: 'from-success-400/20 to-success-100/20',
                    ripple: 'success-100',
                    badge: 'bg-success-focus text-success-600',
                    gradient: 'bg-dark-success-gradient',
                    tooltip: 'tooltip-success'
                };
            case "High":
                return {
                    bg: 'bg-gradient-start-9',
                    border: 'border-warning-300/30',
                    text: 'text-warning-600',
                    glow: 'warning-glow',
                    pulse: 'warning-pulse',
                    wave: 'from-warning-400/20 to-warning-100/20',
                    ripple: 'warning-100',
                    badge: 'bg-warning-focus text-warning-600',
                    gradient: 'bg-dark-warning-gradient',
                    tooltip: 'tooltip-warning'
                };
            case "Critical":
                return {
                    bg: 'bg-gradient-start-5',
                    border: 'border-danger-300',
                    text: 'text-danger-600',
                    glow: 'danger-glow',
                    pulse: 'danger-pulse',
                    wave: 'from-danger-400 to-danger-100',
                    ripple: 'danger-100',
                    badge: 'bg-danger-focus text-danger-600',
                    gradient: 'bg-dark-danger-gradient',
                    tooltip: 'tooltip-danger'
                };
            default:
                return {
                    bg: 'bg-gradient-start-1',
                    border: 'border-secondary-300/30',
                    text: 'text-secondary-600',
                    glow: 'secondary-glow',
                    pulse: 'secondary-pulse',
                    wave: 'from-secondary-400/20 to-secondary-100/20',
                    ripple: 'secondary-100',
                    badge: 'bg-secondary text-secondary-600',
                    gradient: 'bg-dark-secondary-gradient'
                };
        }
    };

    const getTimeElapsed = (dateString) => {
        if (!dateString) return 'N/A';
        const now = new Date();
        const created = new Date(dateString);
        const diff = now - created;

        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ${hours % 24}h ago`;
        if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
        if (minutes > 0) return `${minutes}m ago`;
        return 'Just now';
    };

    const handleEditComplain = (complaintId, e) => {
        if (e) {
            const button = e.currentTarget;
            const ripple = document.createElement('span');
            const rect = button.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height);
            const x = e.clientX - rect.left - size / 2;
            const y = e.clientY - rect.top - size / 2;

            ripple.style.width = ripple.style.height = `${size}px`;
            ripple.style.left = `${x}px`;
            ripple.style.top = `${y}px`;
            ripple.classList.add('ripple-effect');

            const priority = button.closest('.complaint-card')?.dataset.priority;
            const rippleColor = priority ? getPriorityClasses(priority).ripple : 'primary-100';
            ripple.style.backgroundColor = `var(--${rippleColor})`;

            button.appendChild(ripple);

            setTimeout(() => {
                ripple.remove();
            }, 600);
        }

        // Navigate to the closed/update screen with listPath set to /assigned/list
        navigate(`/closed/update/${complaintId}`, { state: { listPath: '/assigned/list' } });
    };

    const handleResetFilters = () => {
        setSelectedZone('');
        setSelectedCircle('');
        setSelectedDivision('');
        setForestCircles([]);
        setForestDivisions([]);
        setFromDate('');
        setToDate('');
        setSearchTerm('');
    };

    return (
        <div className="basic-data-table">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="card-header bg-transparent border-bottom-0 pb-0">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">
                            Assigned Complaints List
                        </h2>
                        <p className="text-sm text-gray-500">
                            View Zone, Circle & Division wise assigned complaints and move directly to Probe Cases without waiting for a reply
                        </p>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {zoneBreakdown.map((z) => (
                            <span
                                key={z.name}
                                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200"
                            >
                                {z.name}: <b>{z.count}</b>
                            </span>
                        ))}
                        <span className="px-4 py-2 rounded-full text-xs font-semibold bg-success-100/30 text-success-800 border border-success-300">
                            Total: <b>{complaints.length}</b>
                        </span>
                    </div>
                </div>
            </div>

            <div className="card-body pt-3">
                {/* Filter toolbar — Search + Cascading Zone -> Circle -> Division + Dates */}
                <div className="mb-4 p-16 radius-12 bg-neutral-50 border border-neutral-200">
                    <div className="row g-3 align-items-end">
                        <div className="col-lg-3 col-md-6 col-sm-12">
                            <label htmlFor="assigned-search" className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:magnify" /> Search
                            </label>
                            <div className="position-relative">
                                <Icon
                                    icon="mdi:magnify"
                                    className="position-absolute top-50 translate-middle-y text-muted"
                                    style={{ left: '14px', fontSize: '20px', pointerEvents: 'none' }}
                                />
                                <input
                                    id="assigned-search"
                                    type="search"
                                    className="form-control ps-5 py-2 bg-white"
                                    placeholder="Search by #, name, or phone"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        {/* Zone dropdown */}
                        <div className="col-lg-2 col-md-4 col-sm-6">
                            <label className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:map-marker-radius" /> Forest Zone
                            </label>
                            <select
                                className="form-select py-2 bg-white"
                                value={selectedZone}
                                onChange={(e) => handleZoneChange(e.target.value)}
                            >
                                <option value="">All Zones</option>
                                {forestZones.map((z) => (
                                    <option key={z._id} value={z._id}>{z.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Circle dropdown */}
                        <div className="col-lg-2 col-md-4 col-sm-6">
                            <label className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:map-marker-path" /> Forest Circle
                            </label>
                            <select
                                className="form-select py-2 bg-white"
                                value={selectedCircle}
                                disabled={!selectedZone}
                                onChange={(e) => handleCircleChange(e.target.value)}
                            >
                                <option value="">{selectedZone ? 'All Circles' : 'Select Zone first'}</option>
                                {forestCircles.map((c) => (
                                    <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Division dropdown */}
                        <div className="col-lg-2 col-md-4 col-sm-6">
                            <label className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:map-marker" /> Forest Division
                            </label>
                            <select
                                className="form-select py-2 bg-white"
                                value={selectedDivision}
                                disabled={!selectedCircle}
                                onChange={(e) => handleDivisionChange(e.target.value)}
                            >
                                <option value="">{selectedCircle ? 'All Divisions' : 'Select Circle first'}</option>
                                {forestDivisions.map((d) => (
                                    <option key={d._id} value={d._id}>{d.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Dispatched Date From */}
                        <div className="col-lg-2 col-md-4 col-sm-6">
                            <label className="form-label text-sm fw-medium text-secondary-light mb-1 d-flex align-items-center gap-1">
                                <Icon icon="mdi:calendar-start" /> Dispatched From
                            </label>
                            <input
                                type="date"
                                className="form-control py-2 bg-white"
                                value={fromDate}
                                max={toDate || undefined}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>

                        {/* Reset button */}
                        <div className="col-lg-1 col-md-4 col-sm-6">
                            <button
                                type="button"
                                className="btn btn-outline-secondary py-2 w-100 d-flex align-items-center justify-content-center"
                                onClick={handleResetFilters}
                                title="Reset all filters"
                            >
                                <Icon icon="mdi:refresh" className="me-1" /> Reset
                            </button>
                        </div>
                    </div>
                </div>

                {/* Cards Grid */}
                <div className="row">
                    {loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                            <div key={index} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-6">
                                <div className="card h-full overflow-hidden border-0 rounded-xl shadow-xs">
                                    <div className="card-body p-5">
                                        <div className="space-y-3">
                                            <Skeleton height={24} width="160px" />
                                            <Skeleton height={20} width="200px" />
                                            <Skeleton height={16} width="140px" />
                                            <Skeleton height={40} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : complaints.length > 0 ? (
                        complaints.map((complaint) => {
                            const priority = getPriorityClasses(complaint.priority_level);
                            const elapsedFrom = complaint.dispatched_time ? 'dispatch time' : 'time of call';
                            const timeElapsed = getTimeElapsed(complaint.dispatched_time || complaint.time_of_call);
                            const qrCodeUrl = qrCodes[complaint._id];
                            const assignedOfficerName = complaint.assigned_to
                                ? `${complaint.assigned_to.first_name || ''} ${complaint.assigned_to.last_name || ''}`.trim()
                                : 'Unassigned';
                            const assignedDesignation = complaint.assigned_to?.designation || 'DCF';
                            const zoneName = complaint.complaint_forest_zone_id?.name || 'N/A';
                            const circleName = complaint.complaint_forest_circle_id?.name || 'N/A';
                            const divisionName = complaint.complaint_forest_division_id?.name || 'N/A';

                            return (
                                <div key={complaint._id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-6">
                                    <div
                                        className={`complaint-card card h-full overflow-hidden border ${priority.bg} ${priority.border} rounded-xl bg-white/80 backdrop-blur-sm relative ${priority.glow} ${priority.pulse}`}
                                        onClick={(e) => handleEditComplain(complaint._id, e)}
                                        data-priority={complaint.priority_level}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className={`absolute top-0 left-0 w-full h-1 ${priority.gradient}`}></div>

                                        <div className="card-body p-5">
                                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start w-100">
                                                <div className="flex-grow-1 me-md-3 mb-3 mb-md-0">
                                                    <div className="d-flex flex-wrap align-items-center mb-2">
                                                        <h3 className="text-lg font-bold text-gray-900 mb-0 me-3" style={{ whiteSpace: 'nowrap' }}>
                                                            #{complaint.complaint_number || 'N/A'}
                                                        </h3>
                                                        <div className="d-flex flex-column">
                                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                                <span className="badge text-xs font-semibold px-3 py-1 rounded-full text-white bg-primary-600" style={{ backgroundColor: '#2563eb' }}>
                                                                    <Icon icon="mdi:account-arrow-right" className="me-1" /> Assigned
                                                                </span>
                                                                <span className={`badge text-xs font-semibold ${priority.gradient} px-3 py-1 rounded-full text-white`}>
                                                                    {complaint.priority_level || 'N/A'}
                                                                </span>
                                                            </div>
                                                            <span className={`text-xs font-semibold ${priority.text} d-flex align-items-center`}>
                                                                <Icon icon="mdi:clock-outline" className="me-1" style={{ lineHeight: 1 }} />
                                                                Elapsed from {elapsedFrom} {timeElapsed}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <table className="w-100 mb-3">
                                                        <tbody>
                                                            <tr className="border-top">
                                                                <td className="py-2 text-muted fw-medium w-25" style={{ whiteSpace: 'nowrap' }}>
                                                                    <Icon icon="bi:building" className="me-2 text-muted align-middle" />
                                                                    <span className="align-middle">Department:</span>
                                                                </td>
                                                                <td className="py-2">
                                                                    <span className="text-body">{complaint.dept_id?.name || 'Forestry & Wildlife'}</span>
                                                                </td>
                                                            </tr>
                                                            <tr className="border-top">
                                                                <td className="py-2 text-muted fw-medium w-25" style={{ whiteSpace: 'nowrap' }}>
                                                                    <Icon icon="bi:tag" className="me-2 text-muted align-middle" />
                                                                    <span className="align-middle">Nature:</span>
                                                                </td>
                                                                <td className="py-2">
                                                                    <span className="text-body">{complaint.complaint_nature_id?.name || 'N/A'}</span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {qrCodeUrl && (
                                                    <div className="flex-shrink-0">
                                                        <a
                                                            href={`${APP_BASE_URL}/complains/view/${complaint._id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none text-reset"
                                                            style={{ zIndex: 9 }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <div
                                                                className="qr-code-container"
                                                                style={{
                                                                    width: '90px',
                                                                    height: '90px',
                                                                    background: 'white',
                                                                    padding: '4px',
                                                                    borderRadius: '6px',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <img
                                                                    src={qrCodeUrl}
                                                                    alt="Complaint QR Code"
                                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                                />
                                                            </div>
                                                        </a>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Forest Hierarchy Tags: Zone -> Circle -> Division */}
                                            <div className="d-flex flex-wrap gap-1 mt-2 mb-3">
                                                <span className="badge bg-warning-50 text-warning-700 border border-warning-200 px-2 py-1 text-xs">
                                                    <Icon icon="mdi:map-marker-radius" className="me-1" />Zone: <b>{zoneName}</b>
                                                </span>
                                                <span className="badge bg-info-50 text-info-700 border border-info-200 px-2 py-1 text-xs">
                                                    <Icon icon="mdi:map-marker-path" className="me-1" />Circle: <b>{circleName}</b>
                                                </span>
                                                <span className="badge bg-success-50 text-success-700 border border-success-200 px-2 py-1 text-xs">
                                                    <Icon icon="mdi:map-marker" className="me-1" />Div: <b>{divisionName}</b>
                                                </span>
                                            </div>

                                            <div className="border-top pt-3">
                                                <table className="table table-responsive scroll-sm mb-0">
                                                    <tbody>
                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{ whiteSpace: 'nowrap' }}>
                                                                <Icon icon="bi:person-badge" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Assigned Officer:</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                <TooltipTextPopupCustom
                                                                    text={`${assignedOfficerName} (${assignedDesignation})`}
                                                                    truncateLength={24}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{ whiteSpace: 'nowrap' }}>
                                                                <Icon icon="bi:telephone" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Officer Phone:</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{ whiteSpace: 'nowrap' }}>
                                                                <span>{complaint.assigned_to?.phone_no || 'N/A'}</span>
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{ whiteSpace: 'nowrap' }}>
                                                                <Icon icon="bi:person" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">Complainant:</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                <TooltipTextPopupCustom
                                                                    text={complaint.complainant_name || 'N/A'}
                                                                    truncateLength={18}
                                                                    placement="right"
                                                                    customClass={`${priority.tooltip}`}
                                                                />
                                                            </td>
                                                        </tr>

                                                        <tr className="border-bottom">
                                                            <td className={`py-2 text-muted fw-medium ${priority.bg}`} style={{ whiteSpace: 'nowrap' }}>
                                                                <Icon icon="bi:geo-alt" className="me-2 text-muted align-middle" />
                                                                <span className="align-middle">District / Tehsil:</span>
                                                            </td>
                                                            <td className={`py-2 ${priority.bg}`} style={{ whiteSpace: 'nowrap' }}>
                                                                <span>{complaint.complaint_district_id?.name || 'N/A'} / {complaint.complaint_tehsil_id?.name || 'N/A'}</span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>

                                            <div className="mt-3 pt-2 d-flex justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-warning text-white d-flex align-items-center gap-1 px-3 py-1.5"
                                                    onClick={(e) => handleEditComplain(complaint._id, e)}
                                                >
                                                    <Icon icon="mdi:magnify-scan" /> Move to Probe / Analyze
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-12 py-5 text-center text-muted">
                            <Icon icon="mdi:clipboard-text-search-outline" style={{ fontSize: '48px', color: '#94a3b8' }} />
                            <h5 className="mt-2 text-gray-700">No assigned complaints found</h5>
                            <p className="text-sm text-gray-500">
                                {selectedZone || selectedCircle || selectedDivision || searchTerm
                                    ? 'Try adjusting your Zone/Circle/Division or search filters'
                                    : 'There are currently no complaints in assigned status'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TableDataLayerAssigned;
